import { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import Navbar from '../components/common/NavBar';
import MiniHero from '../components/common/MiniHero';
import Footer from '../components/common/Footer';
import { get } from '../utils/api';
import { getUserLanguage } from '../utils/language';
import { IMAGE_BASE_URL } from '../config';

const CarsGallery = () => {
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const language = getUserLanguage();

  const { ref: sectionRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const headers = { 'accept-language': language };
        const data = await get('/cars', headers);
        const sortedCars = data.sort((a, b) => a.display_order - b.display_order);
        setCars(sortedCars);
      } catch (error) {
        console.error('[CarsGallery] Error fetching cars:', error);
        setError(language === 'cs' ? 'Nepodařilo se načíst data o autech' : 'Failed to load cars data');
      } finally {
        setLoading(false);
      }
    };
    fetchCars();
  }, [language]);

  if (loading) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Galerie vozidel' : 'Cars Gallery'} />
        <div className="flex justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-black">
        <Navbar language={language} />
        <MiniHero title={language === 'cs' ? 'Galerie vozidel' : 'Cars Gallery'} />
        <div className="text-white text-center py-20">{error}</div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <Navbar language={language} />
      <MiniHero title={language === 'cs' ? 'Galerie vozidel' : 'Cars Gallery'} />
      <section className="py-20 bg-dark-gray relative" ref={sectionRef}>
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {cars.map((car, index) => {
              const carData = car.translations && car.translations[language]
                ? car.translations[language]
                : (car.translations && car.translations.en
                  ? car.translations.en
                  : { name: '', description: '', specs_1: '', specs_2: '', specs_3: '', specs_4: '' });
              return (
                <div
                  key={car.id}
                  className={`drift-card group smoke-effect ${inView ? 'animate-fade-in' : 'opacity-0'}`}
                  style={{ animationDelay: `${0.2 * index}s` }}
                >
                  <Link to={`/car/${car.slug}`} className="block">
                    <div className="relative h-56 overflow-hidden">
                      <div className="absolute inset-0 flex items-center justify-center">
                        <img
                          src={car.image_path ? `${IMAGE_BASE_URL}${car.image_path}` : '/images/cars/placeholder.png'}
                          alt={carData.name || 'Car'}
                          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                          loading="lazy"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = '/images/cars/placeholder.png';
                          }}
                        />
                      </div>
                      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent"></div>
                    </div>
                  </Link>
                  <div className="p-6">
                    <h3 className="text-xl font-title font-bold text-white mb-2 group-hover:text-red-500 transition-colors">
                      {carData.name || (language === 'cs' ? 'Bezejmenné auto' : 'Unnamed Car')}
                    </h3>
                    <div className="gradient-line mb-4"></div>
                    <ul className="text-gray-400 text-sm space-y-2">
                      <li className="flex items-center">
                        <i className="fas fa-tachometer-alt text-red-500 mr-2"></i>
                        <span>{carData.specs_1 || `${car.horsepower} ${language === 'cs' ? 'koní' : 'hp'}`}</span>
                      </li>
                      <li className="flex items-center">
                        <i className="fas fa-cog text-red-500 mr-2"></i>
                        <span>{carData.specs_2 || (language === 'cs' ? 'Není uvedena' : 'Not specified')}</span>
                      </li>
                      <li className="flex items-center">
                        <i className="fas fa-user text-red-500 mr-2"></i>
                        <span>{carData.specs_3 || (language === 'cs' ? 'Není uvedena' : 'Not specified')}</span>
                      </li>
                      <li className="flex items-center">
                        <i className="fas fa-calendar text-red-500 mr-2"></i>
                        <span>{carData.specs_4 || (language === 'cs' ? 'Není uvedena' : 'Not specified')}</span>
                      </li>
                    </ul>
                    <Link 
                      to={`/car/${car.slug}`} 
                      className="inline-flex items-center mt-4 text-sm text-white hover:text-red-500 transition-colors"
                    >
                      <span>{language === 'cs' ? 'Technické specifikace' : 'Technical Specifications'}</span>
                      <i className="fas fa-arrow-right ml-2 text-xs"></i>
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default CarsGallery;